import React from 'react';
import { Calendar, AlertCircle, CheckCircle } from 'lucide-react';

const UpcomingMilestones = ({ data, onItemClick, loading }) => {
  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const dollars = cents / 100;
    if (dollars >= 1000000) {
      return `$${(dollars / 1000000).toFixed(0)}M`;
    }
    return `$${(dollars / 1000).toFixed(0)}K`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getRiskBadge = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return { label: 'High Risk', color: 'bg-red-100 text-red-800 border-red-200' };
      case 'medium':
        return { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
      case 'low':
      default:
        return { label: 'Low Risk', color: 'bg-green-100 text-green-800 border-green-200' };
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No upcoming milestones in the next 90 days</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {data.map((milestone) => {
        const riskBadge = getRiskBadge(milestone.risk_level);
        
        return (
          <div
            key={milestone.opp_id}
            onClick={() => onItemClick(milestone.opp_id)}
            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm mb-1">
                  {milestone.program_name}
                </h3>
                <p className="text-xs text-gray-500">{milestone.oem}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium border ${riskBadge.color}`}>
                {riskBadge.label}
              </span>
            </div>

            {/* SOP Date */}
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                SOP: {formatDate(milestone.sop_date)}
              </span>
              <span className="text-xs text-gray-500">
                ({milestone.days_until_sop} days)
              </span>
            </div>

            {/* Contract Value */}
            <div className="text-sm text-gray-600 mb-2">
              Contract Value: <span className="font-semibold">{formatCurrency(milestone.contract_value)}</span>
            </div>

            {/* Action Items */}
            {milestone.action_items && milestone.action_items.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                {milestone.action_items.map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-2 text-xs text-orange-600">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UpcomingMilestones;
